import { ScanDetailFormattedText } from '@/components/ScanDetailFormattedText';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTruncationObserver } from '@/hooks/useTruncationObserver';
import { cn } from '@/lib/utils';

interface OccurrenceContentCellProps {
  /** Display-ready location (MCP prefix / preview transforms already applied). */
  location: string;
  className?: string;
}

/**
 * Content cell of the alert occurrences table (KIR-4749).
 *
 * Long locations used to grow the row to the full height of the text. The
 * preview is now capped at ~3 formatted lines with a bottom fade, and the full
 * value is shown in a scrollable tooltip on hover/focus (the expanded row still
 * shows it in the Location section).
 *
 * A `max-height` cap is used instead of `line-clamp`: `ScanDetailFormattedText`
 * emits several block children (prefix, one div per line, suffix), which
 * `line-clamp` does not clamp reliably.
 */
export function OccurrenceContentCell({
  location,
  className,
}: OccurrenceContentCellProps) {
  // Dense table: skip the per-row window resize listener; the element's
  // ResizeObserver still re-checks on layout changes.
  const { truncationRef, isTruncated, checkTruncation } = useTruncationObserver(
    { subscribeWindowResize: false }
  );

  const content = (
    <div
      ref={truncationRef}
      data-testid="occurrence-content-preview"
      tabIndex={isTruncated ? 0 : undefined}
      className={cn(
        // ~3 lines: each formatted line is text-xs (16px) + py-1 (8px).
        'relative max-h-[4.75rem] overflow-hidden rounded bg-muted px-1.5 py-0.5 font-mono text-xs break-all',
        isTruncated &&
          '[mask-image:linear-gradient(to_bottom,black_65%,transparent)]',
        className
      )}
      onMouseEnter={checkTruncation}
      onFocus={checkTruncation}
    >
      <ScanDetailFormattedText location={location} />
    </div>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-lg"
        // The tooltip is portalled, but React events still bubble up the
        // component tree to the row's onClick (toggle expansion).
        onClick={e => e.stopPropagation()}
      >
        {/* Own surface: the formatted lines inherit text color, which would
          be primary-foreground on their light line background otherwise. */}
        <div
          data-testid="occurrence-content-full"
          className="max-h-80 overflow-auto rounded bg-background p-1 text-foreground"
        >
          <ScanDetailFormattedText location={location} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
